import { createClient } from '@/lib/supabase/client';

/**
 * Articles stored in Supabase rather than the author's browser.
 *
 * The editor previously kept drafts in localStorage, which meant an article
 * existed on one device only, disappeared with site data, and was invisible to
 * the server — so a published article could never carry its own title or
 * description into search results.
 */

export interface StoredArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  blocks: unknown[];
  status: 'draft' | 'published';
  seoTitle: string;
  seoDescription: string;
  ogImage: string;
  createdAt: string;
  updatedAt: string;
}

export const ARTICLES_TABLE = 'articles';

/** The key the editor used before this table existed. Kept so a one-time
 *  import can lift anything still sitting in a browser. */
export const LEGACY_DRAFTS_KEY = 'noventi_article_drafts';

export function rowToArticle(row: Record<string, unknown>): StoredArticle {
  return {
    id: String(row.id ?? ''),
    slug: String(row.slug ?? ''),
    title: String(row.title ?? ''),
    excerpt: String(row.excerpt ?? ''),
    category: String(row.category ?? 'General'),
    readTime: String(row.read_time ?? ''),
    blocks: Array.isArray(row.blocks) ? (row.blocks as unknown[]) : [],
    status: row.status === 'published' ? 'published' : 'draft',
    seoTitle: String(row.seo_title ?? ''),
    seoDescription: String(row.seo_description ?? ''),
    ogImage: String(row.og_image ?? ''),
    createdAt: String(row.created_at ?? ''),
    updatedAt: String(row.updated_at ?? ''),
  };
}

export function articleToRow(article: Omit<StoredArticle, 'id' | 'createdAt' | 'updatedAt'>) {
  return {
    slug: article.slug,
    title: article.title,
    excerpt: article.excerpt,
    category: article.category,
    read_time: article.readTime,
    blocks: article.blocks,
    status: article.status,
    seo_title: article.seoTitle,
    seo_description: article.seoDescription,
    og_image: article.ogImage,
    updated_at: new Date().toISOString(),
  };
}

/** Admin: every article, drafts included. */
export async function loadAllArticles(): Promise<{ articles: StoredArticle[]; error: string }> {
  const supabase = createClient();
  if (!supabase) return { articles: [], error: 'Supabase is not configured.' };
  const { data, error } = await supabase
    .from(ARTICLES_TABLE)
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) return { articles: [], error: error.message };
  return { articles: (data ?? []).map(rowToArticle), error: '' };
}

export async function saveArticle(
  article: Omit<StoredArticle, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  // Upsert on slug: the editor does not track whether a piece has been saved
  // before, and the slug is what makes an article the same article.
  const { error } = await supabase
    .from(ARTICLES_TABLE)
    .upsert(articleToRow(article), { onConflict: 'slug' });
  return error ? error.message : null;
}

export async function deleteArticle(id: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(ARTICLES_TABLE).delete().eq('id', id);
  return error ? error.message : null;
}

/**
 * Server-side read for generateMetadata and for rendering a published article.
 *
 * A plain fetch rather than supabase-js so this can run during rendering and be
 * cached by Next. Any failure resolves to null and the caller falls back — a
 * metadata lookup must never be able to break the page.
 */
export async function fetchPublishedArticle(slug: string): Promise<StoredArticle | null> {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !key) return null;
  try {
    const response = await fetch(
      `${url.replace(/\/$/, '')}/rest/v1/${ARTICLES_TABLE}` +
        `?slug=eq.${encodeURIComponent(slug)}&status=eq.published&select=*&limit=1`,
      { headers: { apikey: key, Authorization: `Bearer ${key}` }, next: { revalidate: 300 } }
    );
    if (!response.ok) return null;
    const rows = (await response.json()) as Record<string, unknown>[];
    return rows?.length ? rowToArticle(rows[0]) : null;
  } catch {
    return null;
  }
}

/** Reads anything left in the old localStorage key so it can be moved into the
 *  table once, rather than being stranded on whichever machine wrote it. */
export function readLegacyDrafts(storage: Pick<Storage, 'getItem'>): StoredArticle[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(LEGACY_DRAFTS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (d): d is Record<string, unknown> =>
          !!d && typeof d === 'object' && typeof (d as Record<string, unknown>).slug === 'string'
      )
      .map((d) => ({
        id: String(d.id ?? ''),
        slug: String(d.slug ?? ''),
        title: String(d.title ?? ''),
        excerpt: String(d.excerpt ?? ''),
        category: String(d.category ?? 'General'),
        readTime: String(d.readTime ?? ''),
        blocks: Array.isArray(d.blocks) ? (d.blocks as unknown[]) : [],
        status: d.status === 'published' ? ('published' as const) : ('draft' as const),
        seoTitle: '',
        seoDescription: '',
        ogImage: '',
        createdAt: String(d.createdAt ?? ''),
        updatedAt: String(d.updatedAt ?? ''),
      }));
  } catch {
    return [];
  }
}

/** All published articles, for the public list. Same caching and same
 *  never-throw contract as the single-article read. */
export async function fetchPublishedArticles(): Promise<StoredArticle[]> {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  if (!url || !key) return [];
  try {
    const response = await fetch(
      `${url.replace(/\/$/, '')}/rest/v1/${ARTICLES_TABLE}` +
        `?status=eq.published&select=*&order=updated_at.desc`,
      { headers: { apikey: key, Authorization: `Bearer ${key}` } }
    );
    if (!response.ok) return [];
    const rows = (await response.json()) as Record<string, unknown>[];
    return Array.isArray(rows) ? rows.map(rowToArticle) : [];
  } catch {
    return [];
  }
}
